import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import API_BASE_URL, { API_ENDPOINTS } from '../config';
import { useAuth } from './AuthContext';

export interface Space {
  id: number;
  name: string;
  created_at: string;
}

interface SpaceContextType {
  activeSpace: Space | null;
  spaces: Space[];
  loading: boolean;
  error: string | null;
  setActiveSpace: (space: Space | null) => void;
  refreshSpaces: () => Promise<void>;
  createSpace: (name: string) => Promise<Space | null>;
  updateSpace: (id: number, name: string) => Promise<Space | null>;
  deleteSpace: (id: number) => Promise<void>;
}

const SpaceContext = createContext<SpaceContextType | undefined>(undefined);

export const useSpace = () => {
  const context = useContext(SpaceContext);
  if (context === undefined) {
    throw new Error('useSpace must be used within a SpaceProvider');
  }
  return context;
};

const ACTIVE_SPACE_KEY = 'app-activeSpaceId';

export const SpaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [activeSpace, setActiveSpaceState] = useState<Space | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refreshSpaces = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/spaces`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const fetchedSpaces = data.spaces || [];
        setSpaces(fetchedSpaces);

        // Restore active space from localStorage or default to first space
        const savedSpaceId = localStorage.getItem(ACTIVE_SPACE_KEY);
        if (savedSpaceId) {
          const savedSpace = fetchedSpaces.find((s: Space) => s.id === parseInt(savedSpaceId, 10));
          if (savedSpace) {
            setActiveSpaceState(savedSpace);
          } else if (fetchedSpaces.length > 0) {
            setActiveSpaceState(fetchedSpaces[0]);
          }
        } else if (fetchedSpaces.length > 0) {
          setActiveSpaceState(fetchedSpaces[0]);
        }
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to fetch spaces');
      }
    } catch (err) {
      console.error('[SpaceContext] Error fetching spaces:', err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const setActiveSpace = useCallback((space: Space | null) => {
    setActiveSpaceState(space);
    if (space) {
      localStorage.setItem(ACTIVE_SPACE_KEY, space.id.toString());
    } else {
      localStorage.removeItem(ACTIVE_SPACE_KEY);
    }
  }, []);

  const createSpace = useCallback(async (name: string): Promise<Space | null> => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/api/spaces`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        const data = await response.json();
        const newSpace = data.space;
        setSpaces(prev => [...prev, newSpace]);
        return newSpace;
      } else {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to create space');
      }
    } catch (err) {
      console.error('[SpaceContext] Error creating space:', err);
      throw err;
    }
  }, [token]);

  const updateSpace = useCallback(async (id: number, name: string): Promise<Space | null> => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_BASE_URL}/api/spaces/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        const data = await response.json();
        const updatedSpace = data.space;
        setSpaces(prev => prev.map(s => s.id === id ? updatedSpace : s));
        if (activeSpace?.id === id) {
          setActiveSpaceState(updatedSpace);
        }
        return updatedSpace;
      } else {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to update space');
      }
    } catch (err) {
      console.error('[SpaceContext] Error updating space:', err);
      throw err;
    }
  }, [token, activeSpace]);

  const deleteSpace = useCallback(async (id: number): Promise<void> => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/spaces/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSpaces(prev => {
          const remaining = prev.filter(s => s.id !== id);
          // If we deleted the active space, switch to the first remaining one
          if (activeSpace?.id === id && remaining.length > 0) {
            setActiveSpace(remaining[0]);
          }
          return remaining;
        });
      } else {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to delete space');
      }
    } catch (err) {
      console.error('[SpaceContext] Error deleting space:', err);
      // Re-sync with server in case the DB committed but the response failed
      await refreshSpaces();
      throw err;
    }
  }, [token, activeSpace, refreshSpaces]);

  // Initial fetch
  useEffect(() => {
    if (token) {
      refreshSpaces();
    } else {
      setSpaces([]);
      setActiveSpaceState(null);
    }
  }, [token, refreshSpaces]);

  return (
    <SpaceContext.Provider value={{ 
      activeSpace, 
      spaces, 
      loading, 
      error, 
      setActiveSpace, 
      refreshSpaces, 
      createSpace, 
      updateSpace, 
      deleteSpace 
    }}>
      {children}
    </SpaceContext.Provider>
  );
};
